/**!*******************************************************************
 *
 * Copyright (c) Epic AI GmbH. All rights reserved.
 * Author:  Alexander Claas
 *
 ********************************************************************!*/

import { ActionContext, ActionTree } from "vuex";
import { IInboxState } from "./state";
import { IRootState } from "@/store/modules/root/state";
import { IMutations } from "./mutations";
import { InboxStore, UserStore } from "@/store/constants";
import { getAllTags } from "@/api/inbox/settings/tags/get-all";
import { getAllAnswers } from "@/api/inbox/settings/answers/get-all";
import { getConversation } from "@/api/inbox/get-conversation";
import { store } from "@/store";
import { getAllViews } from "@/api/inbox/settings/views/get-all";
import { getAllFields } from "@/api/inbox/settings/fields/get-all";
import { getUsers } from "@/api/inbox/collaborators/get-users";
import { getGroups } from "@/api/inbox/collaborators/get-groups";
import { getLocation } from "@/api/inbox/conversations/get-location";

export enum Actions {
  LOAD_CURRENT_CONVERSATION = "LOAD_CURRENT_CONVERSATION",
  LOAD_TAGS = "LOAD_TAGS",
  LOAD_ANSWERS = "LOAD_ANSWERS",
  LOAD_VIEWS = "LOAD_VIEWS",
  LOAD_FIELDS = "LOAD_FIELDS",
  LOAD_LOCATION = "LOAD_LOCATION",
  // LOAD_USERS = "LOAD_USERS",
  // LOAD_GROUPS = "LOAD_GROUPS",
}

export type AugmentedActionContext = {
  commit<K extends keyof IMutations>(
    key: K,
    payload: Parameters<IMutations[K]>[1]
  ): ReturnType<IMutations[K]>;
} & Omit<ActionContext<IInboxState, IRootState>, "commit">;

export interface IActions {
  [Actions.LOAD_CURRENT_CONVERSATION]({ commit }: AugmentedActionContext): void;
  [Actions.LOAD_TAGS]({ commit }: AugmentedActionContext): void;
  [Actions.LOAD_ANSWERS]({ commit }: AugmentedActionContext): void;
  [Actions.LOAD_VIEWS]({ commit }: AugmentedActionContext): void;
  [Actions.LOAD_FIELDS]({ commit }: AugmentedActionContext): void;
  [Actions.LOAD_LOCATION]({ commit }: AugmentedActionContext): void;
  // [Actions.LOAD_USERS]({ commit }: AugmentedActionContext): void;
  // [Actions.LOAD_GROUPS]({ commit }: AugmentedActionContext): void;
}

export const actions: ActionTree<IInboxState, IRootState> & IActions = {
  [Actions.LOAD_CURRENT_CONVERSATION]({ commit }) {
    commit(InboxStore.Mutations.SET_CURRENT_CONVERSATION_LOADING, true);
    const unsubscribe = getConversation(
      store.getters[InboxStore.Getters.GET_CURRENT_CONVERSATION_ID],
      (conversation) => {
        if (
          conversation &&
          conversation.id &&
          conversation.id ===
            store.getters[InboxStore.Getters.GET_CURRENT_CONVERSATION_ID]
        )
          commit(InboxStore.Mutations.SET_CURRENT_CONVERSATION, conversation);
        commit(InboxStore.Mutations.SET_CURRENT_CONVERSATION_LOADING, false);
      }
    );
    commit(
      InboxStore.Mutations.SET_CURRENT_CONVERSATION_UNSUBSCRIBE,
      unsubscribe
    );
  },
  [Actions.LOAD_TAGS]({ commit }) {
    getAllTags().then((tags) => {
      commit(InboxStore.Mutations.SET_TAGS, tags);
    });
  },
  [Actions.LOAD_ANSWERS]({ commit }) {
    getAllAnswers().then((answers) => {
      commit(InboxStore.Mutations.SET_ANSWERS, answers);
    });
  },
  [Actions.LOAD_VIEWS]({ commit }) {
    getAllViews().then((views) => {
      commit(InboxStore.Mutations.SET_VIEWS, views);
    });
  },
  [Actions.LOAD_FIELDS]({ commit }) {
    getAllFields().then((fields) => {
      commit(InboxStore.Mutations.SET_FIELDS, fields);
    });
  },
  [Actions.LOAD_LOCATION]({ commit }) {
    getLocation().then((location) => {
      commit(InboxStore.Mutations.SET_LOCATION, location);
    });
  },
  // [Actions.LOAD_USERS]({ commit }) {
  //   if (store.getters[UserStore.Getters.ORGANIZATION_ID]) {
  //     getUsers()
  //       .then((users) => {
  //         commit(InboxStore.Mutations.SET_USERS, users);
  //       })
  //       .catch((err) => {
  //         console.error("error: ", err);
  //         commit(InboxStore.Mutations.SET_USERS, []);
  //       });
  //   } else {
  //     commit(InboxStore.Mutations.SET_USERS, []);
  //   }
  // },
  // [Actions.LOAD_GROUPS]({ commit }) {
  //   if (store.getters[UserStore.Getters.ORGANIZATION_ID]) {
  //     getGroups()
  //       .then((groups) => {
  //         commit(InboxStore.Mutations.SET_GROUPS, groups);
  //       })
  //       .catch((err) => {
  //         console.error("error: ", err);
  //         commit(InboxStore.Mutations.SET_GROUPS, []);
  //       });
  //   } else {
  //     commit(InboxStore.Mutations.SET_GROUPS, []);
  //   }
  // },
};
